/**
 * Linearer Echokompensator im Frequenzbereich (partitionierter Block-NLMS,
 * „MDF“ nach Soo/Pang, Schrittweite nach Valin 2007).
 *
 * Er lernt, wie ihre Stimme vom Lautsprecher ins Mikrofon kommt — Verzögerung,
 * Klang, Nachhall —, sagt das Echo daraus voraus und zieht es ab. Anders als
 * eine vollständige Echounterdrückung (AEC3) hat er **keinen** nachgeschalteten
 * Unterdrücker: Was er nicht vorhersagen kann, bleibt stehen. Genau das
 * braucht das Dazwischenreden. Ein Unterdrücker dämpft jeden Moment, in dem
 * K4ia lauter ist als der Besucher — und am iPhone ist sie das fast immer. Der
 * Besucher verschwände mit ihr.
 *
 * Die Schrittweite folgt der Schätzung, wie viel vom Fehler noch Echo ist
 * (`leck`): Spricht der Besucher, wächst der Fehler, ohne dass das Echo
 * wächst — der Filter lernt dann kaum und verlernt nicht, was er weiß.
 *
 * Reine Rechnung, 16 kHz, Blöcke zu 256 Samples (16 ms), 20 Teile (320 ms Echo — Wiedergabe- und
 * Aufnahmepuffer des Telefons, der Weg durch die Luft und der Nachhall).
 */
export const FILTER_RATE = 16000
const N = 256
const M = 2 * N
const TEILE = 20
/** Schrittweite, solange der Filter noch nichts weiß. */
const MU_ANFANG = 0.8
/** So viele Blöcke (je 16 ms) mit fester Schrittweite, bis die Leck-Schätzung trägt. */
const ANFANG_BLOECKE = 400
const MU_MAX = 0.5
/** Untergrenze der Leck-Schätzung: ganz zu lernen hört er nie auf. */
const LECK_MIN = 0.005
/** Ab dieser Echodämpfung (Leistung, 10 = 10 dB) gilt der Filter als eingelernt … */
const BEREIT_DAEMPFUNG = 10
/**
 * Oder: Es kommt kaum Echo an — Kopfhörer, leiser Lautsprecher. Dann ist der
 * Rest 20 dB unter ihrem eigenen Pegel, und es gibt nichts zu verwechseln.
 */
const KAUM_ECHO = 0.01
/** … wenn es so viele Blöcke in Folge hält, in denen sie spricht (~0,5 s). */
const BEREIT_BLOECKE = 30
/** So viele Blöcke (~1,5 s) Echo, bis ein „bereit ohne Echo“ zurückgenommen wird. */
const ECHO_TAUCHT_AUF = 90
/** Glättung der Leistungen je Frequenz. */
const GLAETTUNG = 0.9

/** Iterative Radix-2-FFT, in place; `inv` rechnet zurück (ohne 1/M). */
class Fft {
  private readonly cos: Float64Array
  private readonly sin: Float64Array
  private readonly rev: Uint32Array
  constructor(private readonly n: number) {
    this.cos = new Float64Array(n / 2)
    this.sin = new Float64Array(n / 2)
    for (let i = 0; i < n / 2; i++) {
      this.cos[i] = Math.cos((2 * Math.PI * i) / n)
      this.sin[i] = -Math.sin((2 * Math.PI * i) / n)
    }
    this.rev = new Uint32Array(n)
    const bits = Math.log2(n)
    for (let i = 0; i < n; i++) {
      let r = 0
      for (let b = 0; b < bits; b++) r |= ((i >> b) & 1) << (bits - 1 - b)
      this.rev[i] = r
    }
  }

  run(re: Float64Array, im: Float64Array, inv = false): void {
    const n = this.n
    for (let i = 0; i < n; i++) {
      const j = this.rev[i]!
      if (j > i) {
        let t = re[i]!
        re[i] = re[j]!
        re[j] = t
        t = im[i]!
        im[i] = im[j]!
        im[j] = t
      }
    }
    for (let groesse = 2; groesse <= n; groesse <<= 1) {
      const halb = groesse >> 1
      const schritt = n / groesse
      for (let start = 0; start < n; start += groesse) {
        for (let k = 0; k < halb; k++) {
          const wr = this.cos[k * schritt]!
          const wi = inv ? -this.sin[k * schritt]! : this.sin[k * schritt]!
          const a = start + k
          const b = a + halb
          const xr = re[b]! * wr - im[b]! * wi
          const xi = re[b]! * wi + im[b]! * wr
          re[b] = re[a]! - xr
          im[b] = im[a]! - xi
          re[a] = re[a]! + xr
          im[a] = im[a]! + xi
        }
      }
    }
  }
}

const BINS = N + 1

export class EchoFilter {
  private readonly fft = new Fft(M)
  /** Filter je Teil, als Spektrum (nur die Hälfte bis Nyquist). */
  private readonly wRe: Float64Array[] = []
  private readonly wIm: Float64Array[] = []
  /** Die letzten TEILE Spektren ihrer Stimme. */
  private readonly xRe: Float64Array[] = []
  private readonly xIm: Float64Array[] = []
  private xAlt = new Float64Array(N)
  private readonly px = new Float64Array(BINS)
  private readonly pe = new Float64Array(BINS)
  private readonly py = new Float64Array(BINS)
  /** Schätzung, welcher Anteil des Fehlers noch Echo ist (0…1). */
  private leck = 0
  private eyKov = 0
  private yyVar = 0
  private bloecke = 0
  /** Welcher Teil als nächster auf 256 Taps beschnitten wird. */
  private beschneiden = 0
  // Arbeitsspeicher
  private readonly re = new Float64Array(M)
  private readonly im = new Float64Array(M)
  private readonly yRe = new Float64Array(BINS)
  private readonly yIm = new Float64Array(BINS)
  private readonly eRe = new Float64Array(BINS)
  private readonly eIm = new Float64Array(BINS)
  // Eingangspuffer für beliebige Blocklängen
  private micPuffer = new Float32Array(4 * N)
  private refPuffer = new Float32Array(4 * N)
  private gefuellt = 0
  private readonly d = new Float64Array(N)
  private readonly x = new Float64Array(N)

  /**
   * Hat der Filter ihr Echo eingelernt? Erst dann taugt `restEcho()` als
   * Maßstab: Am Anfang sagt er nichts voraus, schätzt also auch keinen Rest —
   * dabei ist noch ihr ganzes Echo da. Einmal eingelernt, bleibt es so; der
   * Weg vom Lautsprecher ins Mikrofon ändert sich im Gespräch kaum.
   */
  bereit = false
  /**
   * Nicht lernen — solange geprüft wird, ob jemand dazwischenredet. Sonst
   * versuchte der Filter, die Stimme des Besuchers aus ihrer vorherzusagen,
   * und verlernte dabei ihr Echo (am Anfang, mit fester Schrittweite, schnell).
   */
  angehalten = false
  private mitteD = 0
  private mitteE = 0
  private mitteX = 0
  private inFolge = 0
  /** Bereit nur, weil kein Echo kam — nicht, weil er es gelernt hätte. */
  private nurOhneEcho = false
  private echoTaucht = 0

  /** Vorhergesagtes Echo seit dem letzten `restEcho()`: Leistung, Samples. */
  private summeY = 0
  private anzahlY = 0

  /** Zuletzt: Mikrofon, vorhergesagtes Echo, Rest (Leistungen je Block) — zum Prüfen. */
  letzte = { mic: 0, echo: 0, rest: 0, leck: 0 }

  constructor() {
    for (let p = 0; p < TEILE; p++) {
      this.wRe.push(new Float64Array(BINS))
      this.wIm.push(new Float64Array(BINS))
      this.xRe.push(new Float64Array(BINS))
      this.xIm.push(new Float64Array(BINS))
    }
  }

  /**
   * Mikrofon und ihre Stimme, Sample für Sample übereinander, beide 16 kHz.
   * Zurück kommt das Mikrofon ohne das vorhergesagte Echo, in 256er-Schritten.
   */
  verarbeiten(mic: Float32Array, ref: Float32Array): Float32Array {
    const noetig = this.gefuellt + mic.length
    if (noetig > this.micPuffer.length) {
      const groesser = (p: Float32Array) => {
        const neu = new Float32Array(Math.max(noetig, 2 * p.length))
        neu.set(p.subarray(0, this.gefuellt))
        return neu
      }
      this.micPuffer = groesser(this.micPuffer)
      this.refPuffer = groesser(this.refPuffer)
    }
    this.micPuffer.set(mic, this.gefuellt)
    this.refPuffer.set(ref.subarray(0, mic.length), this.gefuellt)
    if (ref.length < mic.length) this.refPuffer.fill(0, this.gefuellt + ref.length, noetig)
    this.gefuellt = noetig

    const bloecke = Math.floor(this.gefuellt / N)
    const raus = new Float32Array(bloecke * N)
    for (let b = 0; b < bloecke; b++) {
      for (let i = 0; i < N; i++) {
        this.d[i] = this.micPuffer[b * N + i]!
        this.x[i] = this.refPuffer[b * N + i]!
      }
      raus.set(this.block(this.d, this.x), b * N)
    }
    const rest = this.gefuellt - bloecke * N
    this.micPuffer.copyWithin(0, bloecke * N, this.gefuellt)
    this.refPuffer.copyWithin(0, bloecke * N, this.gefuellt)
    this.gefuellt = rest
    return raus
  }

  private block(d: Float64Array, x: Float64Array): Float32Array {
    const { re, im, fft } = this
    // Spektrum ihrer Stimme: voriger und jetziger Block (Overlap-Save).
    for (let i = 0; i < N; i++) {
      re[i] = this.xAlt[i]!
      re[N + i] = x[i]!
      im[i] = 0
      im[N + i] = 0
    }
    for (let i = 0; i < N; i++) this.xAlt[i] = x[i]!
    fft.run(re, im)
    // Ältestes Spektrum weiterreichen.
    const altRe = this.xRe.pop()!
    const altIm = this.xIm.pop()!
    for (let k = 0; k < BINS; k++) {
      altRe[k] = re[k]!
      altIm[k] = im[k]!
    }
    this.xRe.unshift(altRe)
    this.xIm.unshift(altIm)

    // Echo vorhersagen: Summe über die Teile.
    this.yRe.fill(0)
    this.yIm.fill(0)
    for (let p = 0; p < TEILE; p++) {
      const wr = this.wRe[p]!
      const wi = this.wIm[p]!
      const xr = this.xRe[p]!
      const xi = this.xIm[p]!
      for (let k = 0; k < BINS; k++) {
        this.yRe[k] = this.yRe[k]! + wr[k]! * xr[k]! - wi[k]! * xi[k]!
        this.yIm[k] = this.yIm[k]! + wr[k]! * xi[k]! + wi[k]! * xr[k]!
      }
    }
    this.halbZuVoll(this.yRe, this.yIm)
    fft.run(re, im, true)
    const e = new Float32Array(N)
    const y = new Float64Array(N)
    let sd = 0
    let sy = 0
    let se = 0
    let sxZeit = 0
    for (let i = 0; i < N; i++) {
      y[i] = re[N + i]! / M
      e[i] = d[i]! - y[i]!
      sd += d[i]! * d[i]!
      sy += y[i]! * y[i]!
      se += e[i]! * e[i]!
      sxZeit += x[i]! * x[i]!
    }
    // Eingelernt? Gemessen nur, während sie spricht.
    if (sxZeit / N > 1e-7) {
      this.mitteD = 0.9 * this.mitteD + 0.1 * sd
      this.mitteE = 0.9 * this.mitteE + 0.1 * se
      this.mitteX = 0.9 * this.mitteX + 0.1 * sxZeit
      const gedaempft = this.mitteD > BEREIT_DAEMPFUNG * this.mitteE
      const kaumEcho = this.mitteE < KAUM_ECHO * this.mitteX
      if (!this.bereit) {
        this.inFolge = gedaempft || kaumEcho ? this.inFolge + 1 : 0
        if (this.inFolge >= BEREIT_BLOECKE) {
          this.bereit = true
          this.nurOhneEcho = !gedaempft
        }
      } else if (this.nurOhneEcho) {
        // Bereit, weil kein Echo kam (Kopfhörer) — und jetzt kommt doch welches
        // (Kopfhörer ab). Dann neu lernen. Lange genug gewartet, dass ein
        // Besucher, der dazwischenredet, vorher erkannt ist.
        this.echoTaucht = !kaumEcho && !gedaempft ? this.echoTaucht + 1 : 0
        if (this.echoTaucht >= ECHO_TAUCHT_AUF) {
          this.bereit = false
          this.inFolge = 0
          this.echoTaucht = 0
        }
      }
    }

    // Fehler ins Spektrum (vorne Nullen).
    for (let i = 0; i < N; i++) {
      re[i] = 0
      im[i] = 0
      re[N + i] = e[i]!
      im[N + i] = 0
    }
    fft.run(re, im)
    for (let k = 0; k < BINS; k++) {
      this.eRe[k] = re[k]!
      this.eIm[k] = im[k]!
    }

    // Leistungen je Frequenz und die Leck-Schätzung (Valin, wie Speex): Wie gut
    // erklären die Schwankungen des vorhergesagten Echos die des Fehlers — je
    // Frequenz über die Zeit, über alle Frequenzen summiert.
    let kov = 0
    let varY = 0
    for (let k = 0; k < BINS; k++) {
      const xp = this.xRe[0]![k]! ** 2 + this.xIm[0]![k]! ** 2
      const ep = this.eRe[k]! ** 2 + this.eIm[k]! ** 2
      const yp = this.yRe[k]! ** 2 + this.yIm[k]! ** 2
      this.px[k] = GLAETTUNG * this.px[k]! + (1 - GLAETTUNG) * xp
      this.pe[k] = GLAETTUNG * this.pe[k]! + (1 - GLAETTUNG) * ep
      this.py[k] = GLAETTUNG * this.py[k]! + (1 - GLAETTUNG) * yp
      const dE = ep - this.pe[k]!
      const dY = yp - this.py[k]!
      kov += dE * dY
      varY += dY * dY
    }
    const a = 0.9
    this.eyKov = a * this.eyKov + (1 - a) * kov
    this.yyVar = a * this.yyVar + (1 - a) * varY
    this.leck = this.yyVar > 1e-20 ? Math.max(LECK_MIN, Math.min(1, this.eyKov / this.yyVar)) : 1
    this.bloecke++

    // Lernen, nur wenn ihre Stimme gerade etwas hergibt.
    let sx = 0
    for (let k = 0; k < BINS; k++) sx += this.px[k]!
    if (sx > 1e-8 && !this.angehalten) {
      // Am Anfang feste Schrittweite: Sie lernt schnell (die vorsichtige nach
      // Valin allein bliebe sekundenlang ungenau). Spricht in der Zeit schon
      // jemand dazwischen — der Fehler ist dann viel größer als ihr
      // vorhergesagtes Echo —, lernt er diesen Block nicht, sonst lernte er die
      // fremde Stimme mit. Danach Valin, die bei fremder Stimme von selbst kaum lernt.
      const anfang = this.bloecke < ANFANG_BLOECKE || sy < 1e-10
      const doppelt = anfang && this.bereit && se > 4 * sy
      // Regularisierung: ein Hundertstel der mittleren Leistung. Frequenzen, in
      // denen sie kaum etwas sagt, lernen langsamer — sonst ergäbe dort ein
      // großer Fehler (etwa die Stimme des Besuchers) einen riesigen Schritt,
      // und der Filter schwänge auf.
      const regel = (sx / BINS) * 1e-2 + 1e-10
      for (let p = 0; p < TEILE && !doppelt; p++) {
        const wr = this.wRe[p]!
        const wi = this.wIm[p]!
        const xr = this.xRe[p]!
        const xi = this.xIm[p]!
        for (let k = 0; k < BINS; k++) {
          // Valin: μ ≈ Leck · |Y|² / |E|² — spricht der Besucher, wird |E| groß
          // und μ klein. Am Anfang, solange nichts vorhergesagt wird, fest.
          const mu = anfang
            ? MU_ANFANG
            : Math.min(MU_MAX, (this.leck * this.py[k]!) / (this.pe[k]! + regel))
          // Normiert auf die Leistung über alle Teile — sonst ist der Schritt
          // TEILE-mal zu groß, und der Filter schwingt auf.
          const g = mu / (TEILE * this.px[k]! + regel)
          // W += g · conj(X) · E
          wr[k] = wr[k]! + g * (xr[k]! * this.eRe[k]! + xi[k]! * this.eIm[k]!)
          wi[k] = wi[k]! + g * (xr[k]! * this.eIm[k]! - xi[k]! * this.eRe[k]!)
        }
      }
      // Ein Teil je Block auf seine 256 Taps beschneiden (Gradientenbedingung),
      // sonst wächst Zirkularfaltung in den Filter.
      this.beschneide(this.beschneiden)
      this.beschneiden = (this.beschneiden + 1) % TEILE
    }

    // Notausgang: Ein Echofilter darf das Mikrofon nie lauter machen. Tut er es
    // doch, hat er sich verlaufen — zurück auf null, neu lernen, und diesen
    // Block unverändert durchreichen.
    // Geglättet und erst ab hörbarem Pegel (−60 dBFS): In Silbengrenzen ist der
    // Fehler einzelner Blöcke kurz größer als das leise Mikrofon, das ist kein
    // Verlaufen.
    this.wacheE = 0.8 * this.wacheE + 0.2 * se
    this.wacheD = 0.8 * this.wacheD + 0.2 * sd
    if (this.wacheE > 4 * this.wacheD + 1e-6 * N) {
      this.zuruecksetzen()
      this.wacheE = 0
      this.wacheD = 0
      for (let i = 0; i < N; i++) e[i] = d[i]!
      se = sd
      sy = 0
    }

    this.letzte = { mic: sd / N, echo: sy / N, rest: se / N, leck: this.leck }
    this.summeY += sy
    this.anzahlY += N
    return e
  }

  /**
   * Wie viel ihres Echos nach dem Filter vermutlich noch übrig ist
   * (Effektivwert, seit dem letzten Aufruf): das vorhergesagte Echo mal der
   * Leck-Schätzung. Genau damit vergleicht die Erkennung fürs Dazwischenreden
   * — was darüber liegt, ist nicht K4ia.
   */
  restEcho(): number {
    const p = this.anzahlY > 0 ? this.summeY / this.anzahlY : 0
    this.summeY = 0
    this.anzahlY = 0
    return Math.sqrt(p * this.leck)
  }

  /** Alles vergessen — nach dem Notausgang. */
  private zuruecksetzen(): void {
    for (let p = 0; p < TEILE; p++) {
      this.wRe[p]!.fill(0)
      this.wIm[p]!.fill(0)
    }
    this.pe.fill(0)
    this.py.fill(0)
    this.eyKov = 0
    this.yyVar = 0
    this.leck = 1
    this.bloecke = 0
    this.bereit = false
    this.nurOhneEcho = false
    this.echoTaucht = 0
    this.inFolge = 0
    this.mitteD = 0
    this.mitteE = 0
    this.mitteX = 0
    this.zurueckgesetzt++
  }

  /** Wie oft der Notausgang gegriffen hat — für `?debug`. */
  zurueckgesetzt = 0
  private wacheE = 0
  private wacheD = 0

  private beschneide(p: number): void {
    const { re, im } = this
    this.halbZuVoll(this.wRe[p]!, this.wIm[p]!)
    this.fft.run(re, im, true)
    for (let i = 0; i < M; i++) {
      re[i] = i < N ? re[i]! / M : 0
      im[i] = 0
    }
    this.fft.run(re, im)
    for (let k = 0; k < BINS; k++) {
      this.wRe[p]![k] = re[k]!
      this.wIm[p]![k] = im[k]!
    }
  }

  /** Halbes Spektrum (reelles Signal) in re/im über alle M Frequenzen. */
  private halbZuVoll(hr: Float64Array, hi: Float64Array): void {
    const { re, im } = this
    for (let k = 0; k < BINS; k++) {
      re[k] = hr[k]!
      im[k] = hi[k]!
    }
    for (let k = 1; k < N; k++) {
      re[M - k] = hr[k]!
      im[M - k] = -hi[k]!
    }
  }
}
